import { useCallback, useRef, useState } from "react";
import { failure } from "./behavior";
import type { FailureFeedback } from "./behavior";

export type CommandPhase =
  | "idle"
  | "submitting"
  | "succeeded"
  | "validation-error"
  | "permission-lost"
  | "reconcile-required";

export interface CommandState {
  readonly phase: CommandPhase;
  readonly error: string;
  readonly resultStatus?: string;
}

const initialCommandState: CommandState = {
  phase: "idle",
  error: "",
};

function failurePhase(feedback: FailureFeedback): CommandPhase {
  if (feedback.recovery === "input" || feedback.recovery === "new-file") return "validation-error";
  if (
    feedback.recovery === "login"
    || feedback.recovery === "permission"
    || feedback.recovery === "different-admin"
  ) return "permission-lost";
  return "reconcile-required";
}

export function useCommandState() {
  const [state, setState] = useState<CommandState>(initialCommandState);
  const inFlight = useRef(false);

  const reset = useCallback(() => setState(initialCommandState), []);
  const setValidationError = useCallback((message: string) => {
    setState({ phase: "validation-error", error: message });
  }, []);
  const setReconcileError = useCallback((message: string) => {
    setState({ phase: "reconcile-required", error: message });
  }, []);
  const execute = useCallback(async (
    run: () => Promise<unknown>,
    reconcile?: (result: unknown) => void | Promise<void>,
  ) => {
    if (inFlight.current ||
      state.phase === "submitting"
      || state.phase === "succeeded"
      || state.phase === "permission-lost"
      || state.phase === "reconcile-required"
    ) return;
    inFlight.current = true;
    setState({ phase: "submitting", error: "" });
    try {
      const result = await run();
      const resultStatus = (result as { status?: string } | undefined)?.status;
      try {
        await reconcile?.(result);
      } catch {
        setState({
          phase: "reconcile-required",
          error: "요청은 접수했지만 최신 상태를 조회하지 못했습니다. 다시 실행하지 말고 상태를 확인해 주세요.",
          resultStatus,
        });
        return;
      }
      setState({ phase: "succeeded", error: "", resultStatus });
    } catch (cause) {
      const feedback = failure(cause);
      setState({
        phase: failurePhase(feedback),
        error: feedback.message,
      });
    } finally {
      inFlight.current = false;
    }
  }, [state.phase]);

  return {
    state,
    execute,
    reset,
    setValidationError,
    setReconcileError,
    isSubmitting: state.phase === "submitting",
    isComplete: state.phase === "succeeded",
    isBlocked: state.phase === "permission-lost" || state.phase === "reconcile-required",
  };
}
